import React from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { Heart, Users, MessageCircle, Camera } from "lucide-react"
import useHead from "@src/hooks/useHead"

const Home: React.FC = () => {
  useHead({ title: "Welcome" })
  const features = [
    {
      icon: MessageCircle,
      title: "Life Questions",
      description: "Capture the essence of your being through over 200 carefully designed questions.",
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: Users,
      title: "Conversations",
      description: "Record conversations to preserve your unique way of expressing and communicating.",
      color: "from-green-500 to-green-600",
    },
    {
      icon: Camera,
      title: "Messages",
      description: "Leave letters and special messages for your loved ones on important occasions.",
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: Heart,
      title: "Digital Legacy",
      description: "Create a digital version of yourself that your loved ones can consult forever.",
      color: "from-pink-500 to-pink-600",
    },
  ]

  return (
    <div className="min-h-screen gradient-bg">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center text-white mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Preserve Your
            <span className="block text-legacy-cream">Digital Legacy</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
            Capture the essence of your being and create a digital version that your loved ones can consult
            forever.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn-primary bg-white text-legacy-dark hover:bg-legacy-cream px-8 py-3 text-lg font-semibold"
            >
              Get Started
            </Link>
            <Link
              to="/login"
              className="btn-secondary border-2 border-white text-white hover:bg-white hover:text-legacy-dark px-8 py-3 text-lg font-semibold"
            >
              Sign In
            </Link>
          </div>
        </motion.div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card bg-white/10 backdrop-blur-sm border-white/20 text-white"
              >
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-r ${feature.color} flex items-center justify-center mb-4`}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-white/80">{feature.description}</p>
              </motion.div>
            )
          })}
        </div>

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center text-white"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-legacy-blue rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold">Sign Up</h3>
              <p className="text-white/80">Create your account and start building your digital profile.</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-legacy-blue rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold">Feed the System</h3>
              <p className="text-white/80">
                Answer questions, record conversations, and leave special messages.
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-legacy-blue rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold">Your Legacy Lives</h3>
              <p className="text-white/80">Your loved ones can interact with your digital version forever.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Home
